<template>
  <div class="grid-x align-middle">
    <AtomicUiIcon :icon="csgNotificationBell" size="s" />
    <div class="medium padding-left-xxs padding-right-xxs">Alerts</div>
    <button class="pointer" type="button" aria-label="tooltip" @click="toggleTooltip($event)">
      <AtomicUiIcon :icon="csgToolTip" size="s" />
    </button>
    <AtomicUiToggletip ref="tt" :position="tooltipPosition" :width="tooltipWidth">
      <p class="small alerts-tool-tip-text">
        {{ ALERTS_TOOLTIP_TEXT }}
      </p>
    </AtomicUiToggletip>
    <AtomicUiToggle
      v-model="isToggled"
      class="padding-left-xxs"
      :disabled="isLoading"
      true-value="ON"
      false-value="OFF"
      @change="handleToggleChange"
    />
  </div>
</template>

<script setup>
import { csgNotificationBell, csgToolTip } from '@csgSvgIcons/icons';
import Toast from '@atomic-ui/toast';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { ALERTS_TOOLTIP_TEXT, brand, headers } from 'config';
import { useGetSelectedListStore } from '#stores/getSelectedList';
import { useSendPriceAlertStore } from '#stores/sendPriceAlert';

const getSelectedListStore = useGetSelectedListStore();
const sendPriceAlertStore = useSendPriceAlertStore();

const tt = ref(null);
const isLoading = ref(false);

const wishlistOnSaleNotify = computed(() => getSelectedListStore.selectedListOfUser?.list?.[0]?.onSaleNotify);
const wishlistGuid = computed(() => getSelectedListStore.selectedListOfUser?.list?.[0]?.listGuid);
const isToggled = ref(wishlistOnSaleNotify.value ? 'ON' : 'OFF');
const tooltipPosition = ref('bottom');
const tooltipWidth = ref();

const setTooltipProps = () => {
  tooltipPosition.value = 'left';
  tooltipWidth.value = window.screen.width < 613 ? 250 : null;
};

watch(wishlistOnSaleNotify, (newValue) => {
  isToggled.value = newValue ? 'ON' : 'OFF';
});

onMounted(() => {
  setTooltipProps();
});

const resizeListener = () => setTooltipProps();
window.addEventListener('resize', resizeListener);
onUnmounted(() => {
  window.removeEventListener('resize', resizeListener);
});

const toggleTooltip = (event) => {
  if (tt.value) {
    tt.value.toggle(event);
  }
};
const firedWishlistAlertStates = new Set(); // remembers guid+state
const handleToggleChange = async () => {
  if (isLoading.value) return;

  const state = isToggled.value.toLowerCase(); // 'on' or 'off'
  const key = `${wishlistGuid.value}:${state}`;
  // fire only if we haven't fired this state for this wishlist
  if (!firedWishlistAlertStates.has(key)) {
    fireTag('link', {
      event_name: `wishlist alerts ${state}`,
      link_name: `wishlist alerts ${state}`,
      wishlist_id: wishlistGuid.value,
    });
    firedWishlistAlertStates.add(key);
  }

  isLoading.value = true;

  try {
    const payload = {
      CustomerList: {
        list: [
          {
            onSaleNotify: isToggled.value === 'ON',
          },
        ],
      },
    };

    await sendPriceAlertStore.sendPriceAlert({
      listGuid: wishlistGuid.value,
      payload,
      headers,
    });
  } catch {
    // Revert the toggle state if the API call fails
    isToggled.value = isToggled.value === 'ON' ? 'OFF' : 'ON';
  } finally {
    const toastOptions = {
      placement: 'bottom',
      duration: 3000,
      showCloseIcon: true,
    };
    if (isToggled.value === 'ON') {
      Toast({
        message:
          brand === 'bcom'
            ? "Success! We'll email you if an item goes on sale or is about to sell out."
            : "Success! We'll email you if an item goes on sale.",
        ...toastOptions,
      });
    } else {
      Toast({
        message: 'Your alerts are now turned off',
        ...toastOptions,
      });
    }
    isLoading.value = false;
  }
};
</script>
<style lang="scss" scoped>
.alerts-tool-tip-text {
  color: $white;
}
.pointer {
  cursor: pointer;
}
</style>
